import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { H3, H6 } from '../../../layouts/TextStyles'
import { Box, CircularProgress, Button, Divider, SelectChangeEvent, LinearProgress } from '@mui/material'
import userIcon from '../../../assets/img/avatar.jpg'
import { useState, useEffect } from 'react'
import CustomInput from '../../../components/muiComponents/customInput'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { requiredString } from '../../../utils/validation'
import { curatorProfileApiSlice } from '../../../features/curator/curatorProfileSlice'
import { toast } from 'react-toastify'
import SingleSelect from '../../../components/muiComponents/singleSelect'
import { ui } from '../../../utils/uiTokens'

const ProfilePage = () => {
    const { formatMessage } = useIntl()
    const [photoPreview, setPhotoPreview] = useState<string | null>(null)
    const [originalValues, setOriginalValues] = useState(null)
    const { data: locations = [] } = curatorProfileApiSlice.useFetchCuratorLocationsQuery()
    const { data: curatorData, isFetching } =
        curatorProfileApiSlice.useFetchCabinetDataQuery()
    const [updateCuratorProfile, { isLoading: isUpdating }] =
        curatorProfileApiSlice.useUpdateCuratorProfileMutation()

    const validationSchema = yup.object({
        firstName: requiredString({ formatMessage }),
        lastName: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            profession: '',
            universityName: '',
            studyYear: '',
            birthCityId: '',
            universityCityId: '',
            link: ''
        },
        validationSchema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: async () => handleUpdateCuratorProfile()
    })

    useEffect(() => {
        if (curatorData) {
            const initial = {
                firstName: curatorData.firstName || '',
                lastName: curatorData.lastName || '',
                profession: curatorData.profession || '',
                universityName: curatorData.universityName || '',
                studyYear: curatorData.studyYear || '',
                birthCityId: curatorData.birthCityId?.toString() || '',
                universityCityId: curatorData.universityCityId?.toString() || '',
                link: curatorData.link || ''
            }

            formik.setValues(initial)
            setOriginalValues(initial)   // ← сохраняем исходные значения
        }
    }, [curatorData])

    // UNIVERSAL SELECT HANDLER
    const handleSelectChange = (field: string) =>
        (event: SelectChangeEvent<string[]>) => {
            const v = Array.isArray(event.target.value)
                ? event.target.value[0]
                : event.target.value

            formik.setFieldValue(field, v)
        }

    // PHOTO UPLOAD
    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (file.size > 10 * 1024 * 1024) {
            toast.error(formatMessage({ id: 'filesDescription' }))
            return
        }
        formik.setFieldValue('link', file)
        setPhotoPreview(URL.createObjectURL(file))
    }

    // BUILD FORMDATA CLEANER
    const buildFormData = (values: any) => {
        const fd = new FormData()
        Object.entries(values).forEach(([key, val]: [string, any]) => {
            if (val === undefined || val === null) return
            // в случае файла
            if (key === 'link' && typeof val !== 'string') {
                fd.append('link', val)
                return
            }
            fd.append(key, String(val))
        })
        return fd
    }

    // CHECK IF VALUES ARE EQUAL
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)

    const handleUpdateCuratorProfile = async () => {
        try {
            const formData = buildFormData(formik.values)
            await updateCuratorProfile(formData).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
        } catch { }
    }

    const currentPhotoSrc =
        photoPreview ||
        (typeof formik.values.link === 'string' ? formik.values.link : '') ||
        userIcon

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'ownCabinet' })}</H3>
                    </Title>
                </Header>
                <Box>
                    {isFetching ? (
                        <LinearProgress />
                    ) : (
                        <Box sx={styles.profileBox}>
                            <form onSubmit={formik.handleSubmit}>
                                <Box sx={styles.profileItem}>
                                    <Box>
                                        <img style={{ borderRadius: '24px' }} src={currentPhotoSrc ? currentPhotoSrc : userIcon} alt="user" width={140} height={140} />
                                        <UploadArea>
                                            <input
                                                type="file"
                                                id="file-upload"
                                                hidden
                                                multiple
                                                accept="image/*"
                                                onChange={handlePhotoChange}
                                            />
                                            <label htmlFor="file-upload">
                                                <UploadButton><span style={{ fontSize: '28px', }}>+</span> {formatMessage({ id: 'addImage' })}</UploadButton>
                                            </label>
                                        </UploadArea>
                                    </Box>
                                    <Box sx={styles.profileItemContent}>
                                        <Box sx={{ gridColumn: '1', gridRow: '1' }}>
                                            <H6>
                                                {formatMessage({ id: 'firstName' })}
                                            </H6>

                                            <CustomInput
                                                id="firstName"
                                                name="firstName"
                                                placeholder={formatMessage({
                                                    id: 'firstName',
                                                })}
                                                fullWidth
                                                value={formik.values.firstName}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                helperText={formik.errors.firstName}
                                                error={Boolean(formik.errors.firstName)}
                                                margin="dense"
                                            />
                                        </Box>

                                        <Box sx={{ gridColumn: '2', gridRow: '1' }}>
                                            <H6>
                                                {formatMessage({ id: 'lastName' })}
                                            </H6>
                                            <CustomInput
                                                id="lastName"
                                                name="lastName"
                                                placeholder={formatMessage({
                                                    id: 'lastName',
                                                })}
                                                fullWidth
                                                value={formik.values.lastName}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={Boolean(formik.errors.lastName)}
                                                helperText={formik.errors.lastName}
                                                margin="dense"
                                            />
                                        </Box>
                                        <Box sx={{ gridColumn: '1', gridRow: '2' }}>
                                            <H6>{formatMessage({ id: 'birthCity' })}</H6>

                                            <SingleSelect
                                                id="birthCity"
                                                name="birthCity"
                                                placeholder={formatMessage({ id: 'birthCity' })}
                                                value={formik.values.birthCityId}
                                                handleChange={handleSelectChange('birthCityId')}
                                                menuItems={locations.map((location) => ({
                                                    id: location.id.toString(),
                                                    titleKz: location.titleKz,
                                                    titleRu: location.titleRu,
                                                }))}
                                            />
                                        </Box>

                                    </Box>
                                </Box>

                                <Divider sx={{ my: 3 }} />
                                <Box sx={styles.profileItemContent}>
                                    <Box sx={{ gridColumn: '1', gridRow: '1' }}>
                                        <H6>
                                            {formatMessage({ id: 'profession' })}
                                        </H6>

                                        <CustomInput
                                            id="profession"
                                            name="profession"
                                            placeholder={formatMessage({
                                                id: 'profession',
                                            })}
                                            fullWidth
                                            value={formik.values.profession}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            helperText={formik.errors.profession}
                                            error={Boolean(formik.errors.profession)}
                                            margin="dense"
                                        />
                                    </Box>

                                    <Box sx={{ gridColumn: '2', gridRow: '1' }}>
                                        <H6>
                                            {formatMessage({ id: 'universityName' })}
                                        </H6>

                                        <CustomInput
                                            id="universityName"
                                            name="universityName"
                                            placeholder={formatMessage({
                                                id: 'universityName',
                                            })}
                                            fullWidth
                                            value={formik.values.universityName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            helperText={formik.errors.universityName}
                                            error={Boolean(formik.errors.universityName)}
                                            margin="dense"
                                        />
                                    </Box>

                                    <Box sx={{ gridColumn: '1', gridRow: '2' }}>
                                        <H6>{formatMessage({ id: 'course' })}</H6>

                                        <SingleSelect
                                            id="studyYear"
                                            name="studyYear"
                                            placeholder={formatMessage({ id: 'course' })}
                                            value={formik.values.studyYear}
                                            handleChange={handleSelectChange('studyYear')}
                                            menuItems={[{ id: 1, name: '1' }, { id: 2, name: '2' }, { id: 3, name: '3' }, { id: 4, name: '4' }, { id: 5, name: '5' }]}
                                        />
                                    </Box>

                                    <Box sx={{ gridColumn: '2', gridRow: '2' }}>
                                        <H6>{formatMessage({ id: 'universityCity' })}</H6>

                                        <SingleSelect
                                            id="universityCityName"
                                            name="universityCityName"
                                            placeholder={formatMessage({ id: 'universityCity' })}
                                            value={formik.values.universityCityId}
                                            handleChange={handleSelectChange('universityCityId')}
                                            menuItems={locations.map((loc) => ({
                                                id: loc.id.toString(),
                                                titleKz: loc.titleKz,
                                                titleRu: loc.titleRu,
                                            }))}
                                        />
                                    </Box>
                                </Box>
                            </form>
                        </Box>
                    )}
                    <Box sx={styles.actionButtons}>
                        <Button
                            variant="outlined"
                            onClick={() => originalValues && formik.setValues(originalValues)}
                            disabled={isUpdating}
                        >
                            {formatMessage({ id: 'cancel' })}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={() => formik.handleSubmit()}
                            disabled={
                                isUpdating ||
                                !originalValues ||
                                isEqual(formik.values, originalValues) // ← нет изменений → disable
                            }
                        >
                            {isUpdating ? <CircularProgress size={20} /> : formatMessage({ id: 'save' })}
                        </Button>
                    </Box>
                </Box>
            </Content>
        </Wrapper>
    )

}

export default ProfilePage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
const UploadArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const UploadButton = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 11px 16px;
  color: ${ui.pink};
  cursor: pointer;
`

const styles = {
    profileBox: {
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        maxWidth: '900px',
        maxHeight: '500px',
        border: `1px solid ${ui.border}`,
        borderRadius: '12px',
    },
    profileItem: {
        display: 'flex',
        gap: '16px',
    },
    profileItemContent: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
        width: '100%',
    },
    actionButtons: {
        display: 'flex',
        gap: '16px',
        marginTop: '24px',
    },
}
